import { useState, MouseEvent } from "react";
import { RequestStatus } from "../../Request/types";
import { EventProps } from 'react-big-calendar'
import ReactDOM from 'react-dom';
import { Typography } from "@mui/material";
import { requestStatusToColor, capitalizeFirstLetter } from "../../../../utils/agenda";
import { requestAppointmetToIcon } from "../../../../hooks";
import { researcherFullName } from "../../../../utils";
import { Translate } from "react-localize-redux";
import BillingLink from '../../../../components/BillingLink';
import QuickPayButton from '../../../../components/QuickPayButton';
import { useSelector } from 'react-redux';
import { ReduxStore } from '../../../../constants/types';

interface MyEvent {
    title: string;
    start: Date;
    end: Date;
    reason?: string;
    notes?: string;
    surgeryIndication?: string;
    surgeon?: string;
    surgicalTeam?: string[];
    serviceDescription?: string;
    overlapCount?:number;
    uuidPatient?: string;
    billUuid?: string | null;
    onQuickPaySuccess?: () => void;
    type: RequestStatus;
  }

export const eventStyleGetter = (event: MyEvent) => {
    
    const backgroundColor = requestStatusToColor(event.type);

    // Set color based on event type or other criteria
    const style = {
        backgroundColor: backgroundColor,
        borderRadius: '5px',
        border: '1px solid white',
        color: 'black',
        display: 'block',
        width: '20%!important', 
        marginLeft: '10px',
    };

    return {
        style,
    };
  };

export const CustomEvent: React.FC<EventProps<MyEvent>> = ({event}) => {
    const { title, reason, notes, surgeryIndication, surgeon, surgicalTeam, serviceDescription, uuidPatient, billUuid, onQuickPaySuccess } = event;
    const currency = useSelector((state: ReduxStore) => state.investigations.currentInvestigation?.billingInfo?.currency ?? 'FCFA');
    const uuidInvestigation = useSelector((state: ReduxStore) => state.investigations.currentInvestigation?.uuid ?? '');
    const languageCode = useSelector((state: any) => state.localize?.languages?.[state.localize?.activeLanguage]?.code ?? 'fr');
    const [showTooltip, setShowTooltip] = useState(true);
    const [tooltipPos, setTooltipPos] = useState<{top:number, left:number}>({top: 0, left: 0});

    const enableTooltip = reason || notes || surgeryIndication || surgeon || surgicalTeam || serviceDescription;
    const handleMouseEnter = (e :  MouseEvent<HTMLDivElement>) => {
        setTooltipPos({
            top: e.clientY + window.scrollY + 10, // 10px below the cursor
            left: e.clientX + window.scrollX + 10, // 10px to the right of the cursor
        });
        setShowTooltip(true);
      };
    
      const handleMouseLeave = () => {
        setShowTooltip(false);
      };

    function renderIcon(type: RequestStatus) {
        
        let icon = requestAppointmetToIcon(type)
        if(type === RequestStatus.PENDING_PAYMENT) {
            icon = (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }} onClick={(e) => e.stopPropagation()}>
                    <BillingLink uuidPatient={uuidPatient!} icon={icon} />
                    {billUuid && uuidInvestigation && (
                        <QuickPayButton
                            uuidInvestigation={uuidInvestigation}
                            billUuid={billUuid}
                            currency={currency}
                            languageCode={languageCode}
                            onSuccess={() => onQuickPaySuccess?.()}
                            size="small"
                        />
                    )}
                </span>
            );
        }
        return (
            <div style={{
                position: 'absolute',
                bottom: '2px',
                right: '2px',
                borderRadius: '50%',
                padding: '2px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                color: 'white',
                background: requestStatusToColor(type)
            }}>
                {icon}
            </div>
        );
    }
    return (
        <>
        <div
            onMouseEnter={enableTooltip ? handleMouseEnter : undefined}
            onMouseLeave={enableTooltip ? handleMouseLeave : undefined}
            style={{ overflow: 'visible', height: '100%', width: '100%', position: 'relative' }}
        >
            <span>{title ? capitalizeFirstLetter(title) : ''}</span>
            {renderIcon(event.type)}
        </div>
        {(showTooltip && (enableTooltip)) &&
        ReactDOM.createPortal(
            <div
                className="tooltip"
                style={{
                    position: 'absolute',
                    top: tooltipPos.top,
                    left: tooltipPos.left,
                    backgroundColor: '#fff',
                    border: '1px solid #ccc',
                    padding: '5px',
                    width: '300px',
                    zIndex: 1000,
                }}
            >
            {reason && <Typography variant="body2"><b>Motif:</b> {reason}</Typography>}
            {notes && <Typography variant="body2"><b>Notes:</b> {notes}</Typography>}
            {surgeryIndication && <Typography variant="body2"><b><Translate id="pages.hospital.services.request.surgery_indication" />:</b> {surgeryIndication}</Typography>}
            {surgeon && <Typography variant="body2"><b><Translate id="pages.hospital.services.main_surgeon" />:</b> {researcherFullName(surgeon)}</Typography>}
            {serviceDescription && <Typography variant="body2">{serviceDescription}</Typography>}
            {surgicalTeam && surgicalTeam.length > 0 && <Typography variant="body2"><b><Translate id="pages.hospital.services.anesthesiologist" />:</b> {Array.isArray(surgicalTeam) ? surgicalTeam.map(team => team.name + " " + team.surnames).join(', ') : surgicalTeam}</Typography>}
          </div>,
          document.body
        )}
        </>
    );
  };
